/**
 * VaultPage — the home of the app and the center of the product.
 *
 * The Vault is the user's home for ALL signed items. Two
 * orthogonal sources combine into one view:
 *
 *   1. **Proofs** (`/api/v1/proofs`) — produced by Sign-a-File,
 *      Prove-an-Idea, Create-a-Document wizards. These are the
 *      everyday signed artifacts a regular user generates.
 *   2. **GIs** (`useVaultSnapshot` → `/api/v1/vault/snapshot`) —
 *      produced by the Spark flow. These are signed entities with
 *      lineage, conductors, etc. — the engine's first-class
 *      primitive.
 *
 * Empty state fires only when BOTH are zero. If proofs exist
 * (the common case for regular users), the page renders the
 * RecentProofs panel; if GIs exist, MissionControl renders too.
 *
 * Five states:
 *   - Loading      → skeleton grid + stat-bar stub
 *   - Error        → ErrorState with Retry (snapshot error only;
 *                    proofs failure falls back to local cache)
 *   - Empty        → onboarding hero + demo-seed CTA
 *   - Proofs only  → RecentProofs panel
 *   - GIs (+/- Proofs) → RecentProofs (if any) + MissionControl
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonGrid } from '@/components/LoadingState';
import { RecentProofs } from '@/components/RecentProofs';

import { MissionControl } from '@/features/vault/MissionControl';

import { useProofs } from '@/hooks/useProofs';
import { useResetVault, useSeedDemoData, useVaultSnapshot } from '@/hooks/useVault';

export function VaultPage() {
  const { t } = useTranslation('vault');
  const query = useVaultSnapshot();
  const proofsQuery = useProofs();
  const seed = useSeedDemoData();
  const reset = useResetVault();

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeading />
        <div className="h-[88px] animate-pulse rounded-lg bg-surface-raised" aria-hidden="true" />
        <SkeletonGrid />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeading />
        <ErrorState
          title={t('error.title')}
          body={t('error.body')}
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  const snapshot = query.data;
  const hasGis = snapshot.gis.length > 0;
  const proofs = proofsQuery.data ?? [];
  const hasProofs = proofs.length > 0;
  const hasAnything = hasGis || hasProofs;

  if (!hasAnything) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeading />
        <EmptyState
          icon={
            <svg
              viewBox="0 0 24 24"
              className="h-6 w-6"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" />
            </svg>
          }
          title={t('empty.title')}
          body={t('empty.body')}
          primaryAction={
            <Link to="/spark" className="contents">
              <Button variant="primary">{t('empty.primary_cta')}</Button>
            </Link>
          }
          secondaryAction={
            <Link to="/verify" className="contents">
              <Button variant="secondary">{t('empty.secondary_cta')}</Button>
            </Link>
          }
          tertiaryAction={
            <Button
              variant="ghost"
              onClick={() => void seed.mutate()}
              disabled={seed.isPending}
            >
              {t('empty.tertiary_cta')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeading />

      {/* "Spark a new GI" CTA — always visible when the user has
          signed anything. Placed here (outside MissionControl) so
          it is present even when hasGis=false (e.g. proofs exist
          but the vault snapshot is empty or the upsert is in-flight).
          This fixes the bug where the button disappeared after Spark
          navigated back to /vault with hasGis temporarily false. */}
      {hasAnything ? (
        <div className="flex items-center gap-2">
          <Link to="/spark">
            <Button variant="primary" size="sm" leading={<SparkGlyph />}>
              {t('cta.spark')}
            </Button>
          </Link>
        </div>
      ) : null}

      {/* Proofs first — it's the common everyday surface. */}
      {hasProofs ? <RecentProofs /> : null}

      {hasGis ? (
        <MissionControl
          snapshot={snapshot}
          {...(import.meta.env.DEV
            ? { onResetDemo: () => void reset.mutate() }
            : {})}
        />
      ) : null}
    </div>
  );
}

function SparkGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
      <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" />
    </svg>
  );
}

function PageHeading() {
  const { t } = useTranslation('vault');
  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight text-ink">{t('title')}</h1>
      <p className="mt-1 text-sm text-ink-muted">{t('subtitle')}</p>
    </div>
  );
}
